import React from "react"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,  
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  IconButton,
  Avatar,
} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

type Props = {
  webmiiSocialResults: any[]
}

const socialIcons: Record<string, string> = {
  wikipedia: "https://cdn.simpleicons.org/wikipedia",
  youtube: "https://cdn.simpleicons.org/youtube",
  instagram: "https://cdn.simpleicons.org/instagram",
  linkedin: "https://cdn.simpleicons.org/linkedin",
  facebook: "https://cdn.simpleicons.org/facebook",
  twitter: "https://cdn.simpleicons.org/x",
  reddit: "https://cdn.simpleicons.org/reddit",
  news: "https://cdn.simpleicons.org/googlenews",
  web: "https://cdn.simpleicons.org/googlechrome",
}

function getSectionRows(item: any) {
  const sections = item?.sections || {}

  return Object.entries(sections).map(([sectionName, posts]: [string, any]) => ({
    sectionName,
    posts: Array.isArray(posts) ? posts : [],
    count: Array.isArray(posts) ? posts.length : 0,
  }))
}

export default function WebmiiSocialResultsViewer({
  webmiiSocialResults,
}: Props) {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      {webmiiSocialResults?.map((item: any, index: number) => {
        const profile = item?.profile || {}
        const rows = getSectionRows(item)

        return (
          <Card key={index} elevation={4} sx={{ borderRadius: 0 }}>
            <CardHeader
              title={
                <Typography variant="h5" fontWeight={700}>
                  {profile?.name || "No name"}
                </Typography>
              }
              subheader={
                profile?.wikipediaDescription ? (
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    {profile.wikipediaDescription}
                  </Typography>
                ) : null
              }
            />

            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: 1,
                  rowGap: 1,
                  mb: 3,
                  '& .MuiChip-root': {
                    backgroundColor: '#40A0D9',
                    color: '#fff',
                  },
                }}
              >
                {profile?.score != null && (
                  <Chip label={`Score: ${profile.score}`} variant="outlined" />
                )}
                {profile?.facebookUsername && (
                  <Chip label={`Facebook: ${profile.facebookUsername}`} variant="outlined" />
                )}
                {profile?.instagramUsername && (
                  <Chip label={`Instagram: ${profile.instagramUsername}`} variant="outlined" />
                )}
                {profile?.twitterUsername && (
                  <Chip label={`Twitter: ${profile.twitterUsername}`} variant="outlined" />
                )}
                {profile?.linkedinJob && (
                  <Chip label={`Job: ${profile.linkedinJob}`} variant="outlined" />
                )}
                {profile?.linkedinCompany && (
                  <Chip label={`Company: ${profile.linkedinCompany}`} variant="outlined" />
                )}
                {profile?.youtubeLabel && (
                  <Chip label="YouTube" variant="outlined" />
                )}
              </Box>

              <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
                <Table>
                  <TableBody>
                    {rows.map((row: any) => (
                      <TableRow key={row.sectionName}>
                        <TableCell sx={{ verticalAlign: "top", fontWeight: 600 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {socialIcons[row.sectionName] && (
                              <Avatar
                                src={socialIcons[row.sectionName]}
                                alt={row.sectionName}
                                sx={{ width: 30, height: 30 }}
                              />
                            )}
                            <span>{row.sectionName}</span>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={2}>
                            {row.posts.length === 0 && (
                              <Typography variant="body2">
                                No items
                              </Typography>
                            )}

                            {row.posts.map((post: any, postIndex: number) => (
                              <Paper
                                key={postIndex}
                                elevation={0}
                                sx={{ p: 2, borderRadius: 0 }}
                              >
                                <Stack spacing={1}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 2,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="subtitle1" fontWeight={600}>
                                        {post?.raw?.title || "Untitled"}
                                      </Typography>

                                      <Typography variant="caption" color="text.secondary">
                                        {post?.raw?.domain || "-"}
                                        {post?.timeStamp ? ` • ${post.timeStamp}` : ""}
                                        {post?.created_date ? ` • ${post.created_date}` : ""}
                                      </Typography>
                                    </Box>

                                    {post?.post_url && (
                                      <IconButton
                                        // color="primary"
                                        href={post.post_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="small"
                                      >
                                        <OpenInNewIcon />
                                      </IconButton>
                                    )}
                                  </Box>

                                  {post?.text && (
                                    <Typography>
                                      {post.text}
                                    </Typography>
                                  )}
                                  {(post?.images_url?.length || 0) > 0 && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 1 }}>
                                      {post.images_url.map((image: string, imageIndex: number) => (
                                        <Box
                                          key={imageIndex}
                                          component="img"
                                          src={image}
                                          alt={`image-${imageIndex}`}
                                          sx={{
                                            width: 150,
                                            height: 130,
                                            objectFit: "contain",
                                            bgcolor: "#fff",
                                            p: 0.5,
                                            borderRadius: 1,
                                            display: "block",
                                          }}
                                        />
                                      ))}
                                    </Stack>
                                  )}
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {(post?.images_url?.length || 0) > 0 && (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={`Images: ${post.images_url.length}`}
                                      />
                                    )}
                                    {(post?.videos_url?.length || 0) > 0 && (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={`Videos: ${post.videos_url.length}`}
                                      />
                                    )}

                                    {(post?.external_link_url?.length || 0) > 0 && (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={`Links: ${post.external_link_url.length}`}
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}